import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { WalletReportFilterModel } from 'src/app/models/wallet/wallet-report-filter.model';
import { ClientService } from 'src/app/services/archive/client.service';
import { ConstructionSiteService } from 'src/app/services/archive/construction-site.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { WalletReportService } from 'src/app/services/wallet/wallet-report.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-wallet-balance',
  templateUrl: './wallet-balance.component.html',
  styleUrls: ['./wallet-balance.component.scss'],
  standalone: false
})
export class WalletBalanceComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  branchId: number = 0;
  changeFilters: boolean = false;
  clientId: number = 0;
  constructionSiteId: number = 0;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formWalletReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listClient: any[] = [];
  listConstructionSite: any[] = [];
  listCostCenter: any[] = [];
  listEnterprise: any[] = [];
  nameWindow: string = 'Informe de Saldos Cartera';
  projectName = ProjectName.projectName;

  constructor(private branchService: BranchService,
    private clientService: ClientService,
    private constructionSiteService: ConstructionSiteService,
    private costCenterService: CostCenterService,
    private enterpriseService: EnterpriseService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryPeriodService: InventoryPeriodService,
    private title: Title,
    private walletReportService: WalletReportService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getLastInventoryPeriod();
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
    this.getFirstEnterpriseByBranch(this.branchId);
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeBranch(branchId: number | null) {
    if (branchId != 0 && branchId != null) {
      this.branchId = branchId;
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    } else {
      this.branchId = 0;
      this.formWalletReportFilter.controls["branchId"].patchValue(0);
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
    this.getAllLists('enterprises');
  }

  changeClient(clientId: number | null) {
    if (clientId != 0 && clientId != null) {
      this.clientId = clientId;
      this.formWalletReportFilter.controls["constructionSiteId"].patchValue(0);
      this.formWalletReportFilter.controls["costCenterId"].patchValue(0);
    } else {
      this.clientId = 0;
      this.formWalletReportFilter.controls["clientId"].patchValue(0);
      this.formWalletReportFilter.controls["constructionSiteId"].patchValue(0);
      this.formWalletReportFilter.controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
    this.getAllLists('constructionSites', 'costCenters');
  }

  changeConstructionSite(constructionSiteId: number | null) {
    if (constructionSiteId != 0 && constructionSiteId != null) {
      this.constructionSiteId = constructionSiteId;
      this.formWalletReportFilter.controls["costCenterId"].patchValue(0);
    } else {
      this.constructionSiteId = 0;
      this.formWalletReportFilter.controls["costCenterId"].patchValue(0);
      this.formWalletReportFilter.controls["constructionSiteId"].patchValue(0);
    }
    this.changeFilter();
    this.getAllLists('costCenters');
  }

  changeCostCenter(costCenterId: number | null) {
    if (costCenterId === 0 || costCenterId === null) {
      this.formWalletReportFilter.controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeEnterprise(enterpriseId: number | null) {
    if (enterpriseId === 0 || enterpriseId === null) {
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  async clear() {
    this.clientId = 0;
    this.constructionSiteId = 0;
    this.changeFilter();
    this.validateForm();
    await this.getLastInventoryPeriod();
    await this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists('enterprises', 'constructionSites', 'costCenters');
    this.getFirstEnterpriseByBranch(this.branchId);
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formWalletReportFilter.controls['dateInitial'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      modelWalletReportFilter.year = parseInt(year);
      modelWalletReportFilter.period = parseInt(period);
      try {
        const response = await firstValueFrom(this.walletReportService.exportWalletReportWalletBalance(modelWalletReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Saldos Cartera.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'enterprises' | 'clients' | 'constructionSites' | 'costCenters'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        enterprises: async () => {
          this.listEnterprise = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
        },
        clients: async () => {
          this.listClient = await firstValueFrom(this.clientService.getAllClientsForSelectAsync());
        },
        constructionSites: async () => {
          this.listConstructionSite = await firstValueFrom(this.constructionSiteService.getAllConstructionSitesByKeysForSelectAsync(this.branchId, this.clientId));
        },
        costCenters: async () => {
          this.listCostCenter = await firstValueFrom(this.costCenterService.getAllCostCentersByKeysForSelectAsync(this.branchId, this.clientId, this.constructionSiteId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.enterprises(),
          lists.clients(),
          lists.constructionSites(),
          lists.costCenters()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      this.branchId = (await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync())).branchId;
      this.formWalletReportFilter.controls["branchId"].patchValue(this.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getFirstEnterpriseByBranch(branchId: number) {
    try {
      const enterprise = await firstValueFrom(this.enterpriseService.getFirstEnterpriseByBranch(branchId));
      this.formWalletReportFilter.controls["enterpriseId"].patchValue(enterprise.enterpriseId);
    } catch (error) {
      console.error("Error al consultar la empresa:", error);
    }
  }

  async getLastInventoryPeriod() {
    try {
      const rentalPeriod = await firstValueFrom(this.inventoryPeriodService.getLastInventoryPeriod());
      this.formWalletReportFilter.controls["dateInitial"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de inventario:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formWalletReportFilter.controls).forEach(key => {
      const control = this.formWalletReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formWalletReportFilter.valid) {
      const dateInitial = this.formWalletReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelWalletReportFilter = this.formWalletReportFilter.value as WalletReportFilterModel;
      modelWalletReportFilter.year = parseInt(year);
      modelWalletReportFilter.period = parseInt(period);

      try {
        this.dataReport = await firstValueFrom(this.walletReportService.walletReportWalletBalance(modelWalletReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Período es obligatorio.'
      },
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      clientId: {
        notNumber: 'Debe seleccionar un Cliente válido.'
      },
      constructionSiteId: {
        notNumber: 'Debe seleccionar una Obra válida.'
      },
      costCenterId: {
        notNumber: 'Debe seleccionar un C.Costos válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formWalletReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formWalletReportFilter = this.formBuilder.group({
      dateInitial: ['', [Validators.required]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      enterpriseId: [0],
      clientId: [0, [CustomValidators.numberOnlyValidator()]],
      constructionSiteId: [0, [CustomValidators.numberOnlyValidator()]],
      costCenterId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
